import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../auth/AuthProvider';

const LabsEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams?.get('id') || '';
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    title: '',
    contentText: '',
  });

  const load = useCallback(async () => {
    if (!postId) return;

    setIsLoading(true);
    setError('');

    try {
      const { data, error: err } = await supabase
        .from('ch_labs_posts')
        .select('id, title, content_text')
        .eq('id', postId)
        .maybeSingle();

      if (err || !data) {
        setError(err?.message || '글을 불러오지 못했습니다.');
        return;
      }

      setForm({
        title: data.title || '',
        contentText: data.content_text || '',
      });
    } finally {
      setIsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!user?.id) return;

    if (!form?.title?.trim() || !form?.contentText?.trim()) {
      setError('제목과 내용을 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if (postId) {
        const { error: err } = await supabase
          .from('ch_labs_posts')
          .update({
            title: form.title.trim(),
            content_text: form.contentText.trim(),
          })
          .eq('id', postId);

        if (err) {
          setError(err?.message || '수정에 실패했습니다.');
          return;
        }

        navigate(`/labs-detail?id=${postId}`);
        return;
      }

      const { data: created, error: err } = await supabase
        .from('ch_labs_posts')
        .insert({
          author_id: user.id,
          author_name: user?.user_metadata?.username || '',
          title: form.title.trim(),
          content_text: form.contentText.trim(),
          is_published: true,
        })
        .select('id')
        .single();

      if (err) {
        setError(err?.message || '등록에 실패했습니다.');
        return;
      }

      navigate(`/labs-detail?id=${created.id}`);
    } finally {
      setIsSaving(false);
    }
  }, [form.contentText, form.title, navigate, postId, user?.id, user?.user_metadata?.username]);

  return (
    <div className="min-h-screen bg-background">
      <Seo title={postId ? '요구 수정' : '요구 등록'} canonicalPath="/labs-editor" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="pt-28 lg:pt-28 pb-20 md:pb-24 lg:pb-16 px-4 sm:px-5 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                {postId ? '요구 수정' : '요구 등록'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">개선되길 바라는 내용을 자유롭게 작성해주세요.</p>
            </div>
          </div>

          {isLoading ? (
            <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 border border-border">
              {error ? (
                <div className="mb-4 flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
                  <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-destructive">{error}</p>
                </div>
              ) : null}

              <Input
                type="text"
                label="제목"
                description="요구사항을 한 줄로 요약해주세요"
                placeholder="예: 챌린지 알림을 더 보기 쉽게 해주세요"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e?.target?.value }))}
                maxLength={80}
              />

              <div className="mt-4">
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                  내용
                </label>
                <p className="text-xs md:text-sm text-muted-foreground caption mb-3">
                  어떤 점이 불편한지, 어떤 방식으로 개선되면 좋은지 적어주세요
                </p>
                <textarea
                  value={form.contentText}
                  onChange={(e) => setForm((prev) => ({ ...prev, contentText: e?.target?.value }))}
                  placeholder="예: 알림에서 어떤 챌린지인지 바로 알 수 있도록 썸네일/텍스트를 개선해주면 좋겠습니다."
                  rows={10}
                  maxLength={3000}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                />
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => navigate(-1)} disabled={isSaving}>
                  취소
                </Button>
                <Button variant="default" iconName="Check" iconPosition="left" onClick={handleSave} loading={isSaving}>
                  저장
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LabsEditor;
